import { toRouteTemplate } from './routeTemplate';

export type BeforeTrackOptions = {
  environment: string;
  applicationName: string;
  getPathname: () => string;
  getOrigin: () => string;
};

const stripSearch = (url: string, currentOrigin: string): string => {
  if (url.startsWith('/')) return url.split('?')[0];

  try {
    const parsed = new URL(url);
    return parsed.origin === currentOrigin ? `${parsed.origin}${parsed.pathname}` : url;
  } catch {
    return url;
  }
};

/**
 * Builds the hook that stamps `application_name`, `environment` and `page`
 * onto every captured event, and strips the query string out of every
 * URL-shaped property Mixpanel adds on its own — `$current_url`, `$pathname`,
 * `current_url_path`/`current_url_search`, `$el_attr__href` — all four of
 * which Mixpanel populates straight from `window.location`/the clicked
 * element, independent of autocapture's own text/attribute settings.
 */
export const buildBeforeTrack =
  ({ applicationName, environment, getOrigin, getPathname }: BeforeTrackOptions) =>
  (eventName: string, properties: Record<string, unknown>): [string, Record<string, unknown>] => {
    const sanitized = { ...properties };
    const { $el_attr__href } = sanitized;

    delete sanitized.current_url_search;

    if (typeof $el_attr__href === 'string') {
      sanitized.$el_attr__href = stripSearch($el_attr__href, getOrigin());
    }

    return [
      eventName,
      {
        ...sanitized,
        application_name: applicationName,
        environment,
        page: toRouteTemplate(getPathname()),
        $current_url: `${getOrigin()}${getPathname()}`,
      },
    ];
  };
